// MACHINERY V2 (Stroje) — compact catalog pagination (M2.8).
//
// Spec: data package §11 — `query.page`, `query.pageSize` ([20,50,100]),
// "showingResults from browseTotalCount + local page math". Artboards 0900 /
// 0901 draw the strip: ‹ 1 2 3 … 302 303 ›.
//
// LOCAL PAGE MATH, DELIBERATELY. The search result carries `totalPages`, and
// the slice even stores it as `browseTotalPages` — but §11 records it as DEAD
// STATE ("NO component reads it") and specifies the count from
// `browseTotalCount` + the LOCAL page size. Keeping the math local means the
// page numbers stay consistent with the page size the user just picked, even
// in the tick before the next response lands. Same choice v1 made.
//
// THE WINDOW: first, last, and a run around the current page, with a single
// ellipsis on each side. `…` is a static separator, never a button — a
// clickable ellipsis implies a destination the user cannot predict.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import TabSelect from "../profile/tabs/TabSelect";
import { CATALOG_PAGE_SIZES } from "./catalogQuery";

export interface CatalogPaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

type Slot = number | "gap";

/** first · … · [current-1, current, current+1] · … · last */
const buildSlots = (page: number, totalPages: number): Slot[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const slots: Slot[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(totalPages - 1, page + 1);

  if (from > 2) slots.push("gap");
  for (let p = from; p <= to; p++) slots.push(p);
  if (to < totalPages - 1) slots.push("gap");
  slots.push(totalPages);
  return slots;
};

const navBtn: React.CSSProperties = {
  minWidth: 28,
  height: 28,
  padding: "0 7px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--card)",
  color: "var(--ink-2)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  display: "inline-grid",
  placeItems: "center",
};

const CatalogPagination: React.FC<CatalogPaginationProps> = ({
  page,
  pageSize,
  totalCount,
  onPage,
  onPageSize,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const lang = getCurrentLanguage();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "14px 0 4px",
        minWidth: 0,
      }}
    >
      {/* The "showing N-M of T" line is NOT here: artboard 0900 puts it ABOVE
          the card grid, right under the category tabs, so `CatalogShowingLine`
          renders it there and this strip carries only the controls. */}
      <span style={{ flex: 1 }} />

      {/* The v2 select primitive, NOT a native <select> (§3 pins the primitive
          library to Radix). `anyLabel` is omitted — there is no "any" page
          size; see the prop's own note in TabSelect.tsx. */}
      <TabSelect
        label={translate("myMachinery:rowsPerPage")}
        value={String(pageSize)}
        options={CATALOG_PAGE_SIZES.map((size) => ({ id: String(size), name: String(size) }))}
        onChange={(value) => {
          if (value) onPageSize(Number(value));
        }}
      />

      <nav
        aria-label={translate("myMachinery:v2.catalog.pagination")}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}
      >
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={translate("myMachinery:v2.catalog.previousPage")}
          style={{ ...navBtn, opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? "default" : "pointer" }}
        >
          ‹
        </button>

        {buildSlots(page, totalPages).map((slot, index) =>
          slot === "gap" ? (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`gap-${index}`}
              aria-hidden
              style={{
                width: 20,
                textAlign: "center",
                color: "var(--faint)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              …
            </span>
          ) : (
            <button
              key={slot}
              type="button"
              onClick={() => onPage(slot)}
              aria-current={slot === page ? "page" : undefined}
              style={{
                ...navBtn,
                background: slot === page ? "var(--mod-accent)" : "var(--card)",
                borderColor: slot === page ? "var(--mod-accent)" : "var(--border)",
                color: slot === page ? "var(--mod-accent-fg)" : "var(--ink-2)",
                fontWeight: slot === page ? 700 : 400,
              }}
            >
              {slot.toLocaleString(lang)}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label={translate("myMachinery:v2.catalog.nextPage")}
          style={{
            ...navBtn,
            opacity: page >= totalPages ? 0.4 : 1,
            cursor: page >= totalPages ? "default" : "pointer",
          }}
        >
          ›
        </button>
      </nav>
    </div>
  );
};

export default CatalogPagination;
